import { useState, forwardRef, useRef, useEffect, useCallback } from 'react';
import { faChevronDown, faChevronUp, faCircleXmark } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DOMPurify from 'dompurify';

import { useDebounceValue } from '@/hooks/use-debounce.hook';
import { removeAccentuation } from '@/utils/formatters/remove-accentuations';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command.component';

export type SelectItem = {
  id: string | number;
  value: string;
  label: string;
};

type SelectProps = {
  items: SelectItem[];
  selectedItem: SelectItem | null;
  placeholder?: string;
  hasError?: boolean;
  disabled?: boolean;
  searchPlaceholder?: string;
  isLoading?: boolean;
  onChange: (item: SelectItem | null) => void;
  dataTestId?: string;
};

const Select = forwardRef<HTMLInputElement, SelectProps>(
  (
    {
      items,
      selectedItem,
      placeholder = 'Selecione uma opção',
      hasError,
      disabled,
      isLoading,
      onChange,
      dataTestId: propDataTestId,
    },
    ref,
  ) => {
    const dataTestId = propDataTestId || 'select-input';

    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState('');
    const inputRef = useRef<HTMLInputElement>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const debouncedInputValue = useDebounceValue(inputValue, 300);

    const setRefs = useCallback((element: HTMLInputElement | null) => {
      inputRef.current = element;
      if (typeof ref === 'function') {
        ref(element);
      } else if (ref) {
        ref.current = element;
      }
    }, [ref]);

    useEffect(() => {
      if (selectedItem) {
        setInputValue(selectedItem.label);
      } else if (!open) {
        setInputValue('');
      }
    }, [selectedItem, open]);

    useEffect(() => {
      const handleClickOutside = (event: MouseEvent) => {
        if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
          setOpen(false);
        }
      };

      if (open) {
        document.addEventListener('mousedown', handleClickOutside);
      }

      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }, [open]);

    const normalizedQuery = removeAccentuation(debouncedInputValue).toLowerCase();
    const filteredItems = items.filter((item) =>
      removeAccentuation(item.label).toLowerCase().includes(normalizedQuery),
    );

    const handleInputChange = (value: string) => {
      const sanitizedValue = DOMPurify.sanitize(value);
      setInputValue(sanitizedValue);
      if (sanitizedValue.length > 0) {
        if (!open) {
          setOpen(true);
        }
      } else {
        setOpen(false);
      }
    };

    const handleInputClick = () => {
      if (disabled) {
        return;
      }
      setOpen((prev) => !prev);
    };

    const handleClearSelection = (e: React.MouseEvent) => {
      e.stopPropagation();
      onChange(null);
      setInputValue('');
      setOpen(false);
      inputRef.current?.focus();
    };

    const handleSelect = (value: string) => {
      const selected = items.find((item) => item.value === value) || null;
      onChange(selected);
      setOpen(false);
    };

    if (isLoading || disabled) {
      return (
        <div className="relative w-full">
          <input
            disabled
            type="text"
            className="w-full border rounded-lg transition-all outline-none min-h-[3rem] bg-gray-100 py-2 px-4 text-gray-400 placeholder:text-gray-400 mt-1 border-gray-300 cursor-not-allowed"
            value={isLoading ? 'Carregando...' : inputValue || placeholder}
            readOnly
          />
          {!isLoading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">
              <FontAwesomeIcon icon={faChevronDown} className="w-4 h-4" />
            </div>
          )}
        </div>
      );
    }

    return (
      <div ref={containerRef} className="relative w-full">
        <div className="relative">
          <input
            key="select-input"
            ref={setRefs}
            type="text"
            data-testid={dataTestId}
            disabled={disabled}
            className={
              `w-full border rounded-lg transition-all outline-none min-h-[3rem]
              bg-white py-2 px-4 text-gray-700 placeholder:text-gray-500 mt-1
              border-gray-300 focus-within:border-gray-500
              ${hasError && !open ? 'border-danger-600' : ''}
              ${selectedItem ? 'pr-10' : ''}
              ${disabled ? 'bg-gray-100 cursor-not-allowed text-gray-400' : ''}`
            }
            placeholder={placeholder}
            value={inputValue}
            onClick={handleInputClick}
            onChange={(e) => {
              handleInputChange(e.target.value);
            }}
          />
          {selectedItem
            ? (
                <button
                  type="button"
                  onClick={handleClearSelection}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors mt-[2px]"
                  aria-label="Limpar seleção"
                >
                  <FontAwesomeIcon icon={faCircleXmark} className="w-4 h-4" />
                </button>
              )
            : (
                <button
                  type="button"
                  onClick={handleInputClick}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition-colors mt-[2px]"
                  aria-label={open ? 'Fechar opções' : 'Abrir opções'}
                  disabled={disabled}
                >
                  <FontAwesomeIcon icon={open ? faChevronUp : faChevronDown} className="w-4 h-4" />
                </button>
              )}
        </div>
        {open && (
          <div className="absolute z-50 w-full p-0 bg-white rounded-lg border border-gray-500 shadow-md">
            <Command shouldFilter={false}>
              <CommandList className="max-h-[300px]">
                {!filteredItems.length && (
                  <CommandEmpty data-testid="empty-state" className="p-4 text-center">
                    <span className="text-base text-gray-900">
                      Nenhum resultado encontrado
                    </span>
                  </CommandEmpty>
                )}
                {filteredItems.length > 0 && (
                  <CommandGroup>
                    {filteredItems.map((item) => (
                      <CommandItem
                        key={`key-select-item-${item.id}${item.value}${item.label}`}
                        value={item.value}
                        onSelect={handleSelect}
                        className="cursor-pointer"
                      >
                        {item.label}
                      </CommandItem>
                    ))}
                  </CommandGroup>
                )}
              </CommandList>
            </Command>
          </div>
        )}
      </div>
    );
  },
);

Select.displayName = 'Select';

export default Select;
